/**
 * Style History Context
 *
 * Provides React context for managing paragraph style change history
 * throughout a styling session. This is a custom tracking mechanism
 * independent of Word's tracked changes API.
 *
 * The StyleHistoryProvider maintains a persistent record of all style
 * changes that survives accept/reject operations, enabling audit trails
 * and reliable change tracking.
 *
 * @see docs/requirements/review-paragraphs/custom-tracking-changes.md
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ClassificationLine, ClassificationResponse } from "src/types";
import {
  createStyleRecords,
  updateRecordsFromClassification,
  markAsAccepted,
  markAsRejected,
  markAsRestyled,
  remapStaleRecords,
  getRecordById,
  getPendingCount,
  getChangedCount,
  getPendingRecords,
  getChangedRecords,
  type ParagraphStyleRecord,
  type StyleChangeEntry,
  type StyleChangeSource,
} from "../services/paragraph-style-history-service";
import { getTagToLocalIdMap, loadStyleRecords, persistStyleRecords } from "src/services";

// ────────────────────────────────────────────────────────────────────────────
// Context Types
// ────────────────────────────────────────────────────────────────────────────

/** Shape of the style history context value. */
export interface StyleHistoryContextValue {
  /** All paragraph style records for the current session. */
  styleRecords: ParagraphStyleRecord[];

  /** True if records have been initialized (after classifier runs). */
  isInitialized: boolean;

  // ── Lifecycle Actions ──

  /**
   * Initializes style records from extracted document lines.
   * Called before classifier runs to capture original state.
   * Pass localIdToTag to populate paragraphTag on each record for cross-session identity.
   */
  initializeRecords: (lines: ClassificationLine[], localIdToTag?: Map<string, string>) => void;

  /**
   * Updates records after classifier returns results.
   * Sets isPending=true for paragraphs where style changed.
   */
  applyClassificationResults: (response: ClassificationResponse) => void;

  /**
   * Clears all records. Called when starting a new styling session.
   */
  clearRecords: () => void;

  // ── Review Actions ──

  /**
   * Marks paragraphs as accepted (approved).
   * Sets isPending=false, adds 'accept' history entry.
   */
  acceptChanges: (ids: string[]) => void;

  /**
   * Marks paragraphs as rejected.
   * Reverts style, increments count, adds 'reject' history entry.
   */
  rejectChanges: (ids: string[], revertedStyles: Map<string, string>) => void;

  /**
   * Marks paragraphs as restyled (manual change).
   * Updates style, increments count, adds 'restyle' history entry.
   */
  restyleChanges: (ids: string[], newStyle: string) => void;

  // ── Query Helpers ──

  /** Gets a single record by its uniqueLocalId. */
  getRecord: (id: string) => ParagraphStyleRecord | undefined;

  /** Gets the count of paragraphs with pending changes. */
  pendingCount: number;

  /** Gets the count of paragraphs that have been modified. */
  changedCount: number;

  /** Gets all records with pending changes. */
  pendingRecords: ParagraphStyleRecord[];

  /** Gets all records that have been modified. */
  changedRecords: ParagraphStyleRecord[];
}

// ────────────────────────────────────────────────────────────────────────────
// Context & Provider
// ────────────────────────────────────────────────────────────────────────────

const StyleHistoryContext = createContext<StyleHistoryContextValue | null>(null);

/**
 * Provides style history state to descendant components.
 *
 * Records are initialized when the classifier runs and updated
 * as the user reviews (accepts/rejects/restyles) paragraphs.
 */
export const StyleHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [styleRecords, setStyleRecords] = useState<ParagraphStyleRecord[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // ── Rehydrate from CustomXmlPart on mount ──

  useEffect(() => {
    Promise.all([loadStyleRecords(), getTagToLocalIdMap()])
      .then(([loaded, tagToLocalId]) => {
        if (loaded.length > 0) {
          const remapped = remapStaleRecords(loaded, tagToLocalId);
          const remappedCount = remapped.filter(
            (r, i) => r.uniqueLocalId !== loaded[i].uniqueLocalId
          ).length;
          setStyleRecords(remapped);
          setIsInitialized(true);
          if (remappedCount > 0) {
            persistStyleRecords(remapped).catch((err) =>
              console.error("[StyleHistory] Failed to persist remapped records:", err)
            );
          }
        }
      })
      .catch((err) => {
        console.error("[StyleHistory] Failed to rehydrate from XML part:", err);
      });
  }, []);

  // ── Lifecycle Actions ──

  const initializeRecords = useCallback(
    (lines: ClassificationLine[], localIdToTag?: Map<string, string>) => {
      const records = createStyleRecords(lines, localIdToTag);
      setStyleRecords(records);
      setIsInitialized(true);
      persistStyleRecords(records).catch((err) =>
        console.error("[StyleHistory] Failed to persist after init:", err)
      );
    },
    []
  );

  const applyClassificationResults = useCallback((response: ClassificationResponse) => {
    setStyleRecords((prev) => {
      const updated = updateRecordsFromClassification(prev, response);
      const pendingCount = updated.filter((r) => r.isPending).length;
      persistStyleRecords(updated).catch((err) =>
        console.error("[StyleHistory] Failed to persist after classification:", err)
      );
      return updated;
    });
  }, []);

  const clearRecords = useCallback(() => {
    setStyleRecords([]);
    setIsInitialized(false);
    persistStyleRecords([]).catch((err) =>
      console.error("[StyleHistory] Failed to persist after clear:", err)
    );
  }, []);

  // ── Review Actions ──

  const acceptChanges = useCallback((ids: string[]) => {
    setStyleRecords((prev) => {
      const updated = markAsAccepted(prev, ids);
      persistStyleRecords(updated).catch((err) =>
        console.error("[StyleHistory] Failed to persist after accept:", err)
      );
      return updated;
    });
  }, []);

  const rejectChanges = useCallback((ids: string[]) => {
    setStyleRecords((prev) => {
      const updated = markAsRejected(prev, ids);
      persistStyleRecords(updated).catch((err) =>
        console.error("[StyleHistory] Failed to persist after reject:", err)
      );
      return updated;
    });
  }, []);

  const restyleChanges = useCallback((ids: string[], newStyle: string) => {
    setStyleRecords((prev) => {
      const updated = markAsRestyled(prev, ids, newStyle);
      persistStyleRecords(updated).catch((err) =>
        console.error("[StyleHistory] Failed to persist after restyle:", err)
      );
      return updated;
    });
  }, []);

  // ── Query Helpers ──

  const getRecord = useCallback((id: string) => getRecordById(styleRecords, id), [styleRecords]);

  // Memoized derived values
  const pendingCount = useMemo(() => getPendingCount(styleRecords), [styleRecords]);
  const changedCount = useMemo(() => getChangedCount(styleRecords), [styleRecords]);
  const pendingRecords = useMemo(() => getPendingRecords(styleRecords), [styleRecords]);
  const changedRecords = useMemo(() => getChangedRecords(styleRecords), [styleRecords]);

  // ── Context Value ──

  const value: StyleHistoryContextValue = useMemo(
    () => ({
      styleRecords,
      isInitialized,
      initializeRecords,
      applyClassificationResults,
      clearRecords,
      acceptChanges,
      rejectChanges,
      restyleChanges,
      getRecord,
      pendingCount,
      changedCount,
      pendingRecords,
      changedRecords,
    }),
    [
      styleRecords,
      isInitialized,
      initializeRecords,
      applyClassificationResults,
      clearRecords,
      acceptChanges,
      rejectChanges,
      restyleChanges,
      getRecord,
      pendingCount,
      changedCount,
      pendingRecords,
      changedRecords,
    ]
  );

  return <StyleHistoryContext.Provider value={value}>{children}</StyleHistoryContext.Provider>;
};

// ────────────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────────────

/**
 * Hook to access style history context.
 *
 * @throws Error if used outside of StyleHistoryProvider
 */
export function useStyleHistory(): StyleHistoryContextValue {
  const context = useContext(StyleHistoryContext);
  if (!context) {
    throw new Error("useStyleHistory must be used within a StyleHistoryProvider");
  }
  return context;
}

// ────────────────────────────────────────────────────────────────────────────
// Re-exports for convenience
// ────────────────────────────────────────────────────────────────────────────

export type { ParagraphStyleRecord, StyleChangeEntry, StyleChangeSource };
